'use client';

import Link from 'next/link';
import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import type { LearnerLearningSummaryDto } from '@vuganawe/types';
import { SKILL_TYPES } from '@vuganawe/types';
import { SyncStatusPill } from '@/components/shell/SyncStatusPill';
import { useT } from '@/i18n/client';
import { getLearningSummary } from '@/lib/api/public';
import { getLearnerToken } from '@/lib/learnerSession';

/**
 * Global learning header shown above every /learn page (catalog, course,
 * lesson). Carries the learner's overall score on top and the key skill areas
 * below — the primary way to dive between lessons.
 */
export function LearningHeader() {
  const t = useT();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [ready, setReady] = useState(false);
  const [signedIn, setSignedIn] = useState(false);
  const [summary, setSummary] = useState<LearnerLearningSummaryDto | null>(null);

  useEffect(() => {
    const token = getLearnerToken();
    setSignedIn(Boolean(token));
    setReady(true);
    if (!token) return;
    void getLearningSummary(token)
      .then(setSummary)
      .catch(() => setSummary(null));
  }, [pathname]);

  const activeSkill = searchParams.get('skill');
  const onCatalogRoot = pathname === '/learn' && !activeSkill;

  return (
    <header className="vn-lh">
      <div className="vn-lh__top vn-container">
        <Link href="/learn" className="vn-lh__brand">
          <span className="vn-lh__brand-mark" aria-hidden="true">
            ◆
          </span>
          {t('header.brand')}
        </Link>

        <SyncStatusPill />

        {ready ? (
          signedIn ? (
            <Link href="/account/learn" className="vn-lh__score" aria-label={t('header.myLearning')}>
              {summary && summary.streakDays > 0 ? (
                <span className="vn-lh__stat">
                  <b>🔥 {summary.streakDays}</b>
                  <i>{t('header.dayStreak')}</i>
                </span>
              ) : null}
              <span className="vn-lh__stat">
                <b>{summary?.progressPercent ?? 0}%</b>
                <i>{t('header.complete')}</i>
              </span>
              <span className="vn-lh__stat">
                <b>
                  {summary?.completedLessons ?? 0}
                  <span className="vn-lh__stat-sub">/{summary?.totalLessons ?? 0}</span>
                </b>
                <i>{t('header.lessons')}</i>
              </span>
              <span className="vn-lh__stat">
                <b>
                  {summary?.averageQuizScore !== null && summary?.averageQuizScore !== undefined
                    ? `${summary.averageQuizScore}%`
                    : '—'}
                </b>
                <i>{t('header.avgQuiz')}</i>
              </span>
              <span className="vn-lh__score-cta" aria-hidden="true">
                {t('header.myLearning')}
              </span>
            </Link>
          ) : (
            <div className="vn-lh__score vn-lh__score--guest">
              <span className="vn-lh__score-note">{t('header.guestNote')}</span>
              <Link href="/account/login?next=/account" className="vn-btn vn-btn--secondary vn-btn--small">
                {t('common.signIn')}
              </Link>
            </div>
          )
        ) : (
          <span className="vn-lh__score" aria-hidden="true" />
        )}
      </div>

      <nav className="vn-lh__areas" aria-label={t('header.allLessons')}>
        <div className="vn-lh__areas-inner vn-container">
          <Link href="/learn" className={`vn-lh__area${onCatalogRoot ? ' is-active' : ''}`}>
            {t('header.allLessons')}
          </Link>
          {SKILL_TYPES.map((skill) => (
            <Link
              key={skill}
              href={`/learn?skill=${skill}`}
              className={`vn-lh__area${activeSkill === skill ? ' is-active' : ''}`}
            >
              {t(`skills.${skill}`)}
            </Link>
          ))}
          <Link
            href="/learn/placement"
            className={`vn-lh__area vn-lh__area--test${pathname === '/learn/placement' ? ' is-active' : ''}`}
          >
            {t('header.findMyLevel')}
          </Link>
          <Link
            href="/learn/games"
            className={`vn-lh__area${pathname === '/learn/games' ? ' is-active' : ''}`}
          >
            {t('header.games')}
          </Link>
        </div>
      </nav>
    </header>
  );
}
